import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, PenLine, Share2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: {
    title: 'What do you want to do with this moment?',
    feel: 'Feel this again',
    send: 'Send something like this',
    share: 'Share this moment',
    rewriting: 'Rewriting this moment…',
  },
  es: {
    title: '¿Qué quieres hacer con este momento?',
    feel: 'Siéntelo otra vez',
    send: 'Envía algo así',
    share: 'Comparte este momento',
    rewriting: 'Reescribiendo este momento…',
  },
  pt: {
    title: 'O que você quer fazer com este momento?',
    feel: 'Sentir isso novamente',
    send: 'Envie algo assim',
    share: 'Compartilhar este momento',
    rewriting: 'Reescrevendo este momento…',
  },
  fr: {
    title: 'Que voulez-vous faire de ce moment ?',
    feel: 'Ressentir à nouveau',
    send: 'Envoyer quelque chose comme ça',
    share: 'Partager ce moment',
    rewriting: 'Réécriture de ce moment…',
  },
  ar: {
    title: 'ماذا تريد أن تفعل بهذه اللحظة؟',
    feel: 'اشعر بهذا مرة أخرى',
    send: 'أرسل شيئًا مثل هذا',
    share: 'شارك هذه اللحظة',
    rewriting: 'إعادة كتابة هذه اللحظة…',
  },
  ja: {
    title: 'この瞬間で何をしたいですか？',
    feel: 'もう一度感じる',
    send: 'こんなメッセージを送る',
    share: 'この瞬間を共有',
    rewriting: 'この瞬間を書き直しています…',
  },
  ko: {
    title: '이 순간으로 무엇을 하시겠어요?',
    feel: '다시 느껴보기',
    send: '비슷한 메시지 보내기',
    share: '이 순간 공유하기',
    rewriting: '이 순간을 다시 쓰는 중…',
  },
  id: {
    title: 'Apa yang ingin kamu lakukan dengan momen ini?',
    feel: 'Rasakan lagi',
    send: 'Kirim sesuatu seperti ini',
    share: 'Bagikan momen ini',
    rewriting: 'Menulis ulang momen ini…',
  },
  hi: {
    title: 'इस पल के साथ आप क्या करना चाहते हैं?',
    feel: 'इसे फिर से महसूस करें',
    send: 'ऐसा ही कुछ भेजें',
    share: 'इस पल को साझा करें',
    rewriting: 'इस पल को फिर से लिखा जा रहा है…',
  },
};

interface MessageActionsSectionProps {
  /** Pre-fill values to drop into /create */
  prefill?: {
    relationship?: string;
    template?: string;
    animationTheme?: string;
    message?: string;
  };
  /** Called when user wants to replay the reveal */
  onReplay: () => void;
  /** Called when user wants to share — should open the share dialog */
  onShare?: () => void;
  /** Hide replay (e.g., owner who just published) */
  hideReplay?: boolean;
}

/**
 * User-triggered action section shown beneath a finished reveal.
 * NEVER auto-navigates — only on explicit tap.
 */
export function MessageActionsSection({
  prefill,
  onReplay,
  onShare,
  hideReplay = false,
}: MessageActionsSectionProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = lc(copy, language);
  const [rewriting, setRewriting] = useState(false);

  const handleSendLikeThis = () => {
    setRewriting(true);
    // brief overlay, then navigate
    setTimeout(() => {
      try {
        if (prefill) {
          sessionStorage.setItem('sayitnow:prefill', JSON.stringify(prefill));
        }
      } catch {
        // ignore storage errors
      }
      navigate('/create?from=moment');
    }, 900);
  };

  return (
    <>
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
        className="relative z-30 px-4 sm:px-6 py-10 sm:py-12 bg-gradient-to-b from-transparent via-background/60 to-background"
        aria-label="Message actions"
      >
        <div className="max-w-md mx-auto text-center space-y-5">
          <h2 className="text-base sm:text-lg font-medium text-foreground/90 tracking-wide">
            {t.title}
          </h2>

          <div className="flex flex-col gap-3">
            {!hideReplay && (
              <Button
                onClick={onReplay}
                variant="outline"
                size="lg"
                className="w-full justify-center gap-2 border-border/60 bg-card/40 backdrop-blur-sm hover:bg-card/70 text-foreground/90"
              >
                <Heart className="h-4 w-4 text-rose-400" fill="currentColor" />
                {t.feel}
              </Button>
            )}

            <Button
              onClick={handleSendLikeThis}
              size="lg"
              className="w-full justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20"
            >
              <PenLine className="h-4 w-4" />
              {t.send}
            </Button>

            {onShare && (
              <Button
                onClick={onShare}
                variant="ghost"
                size="lg"
                className="w-full justify-center gap-2 text-muted-foreground hover:text-foreground"
              >
                <Share2 className="h-4 w-4" />
                {t.share}
              </Button>
            )}
          </div>
        </div>
      </motion.section>

      {/* Rewriting overlay */}
      <AnimatePresence>
        {rewriting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-background/85 backdrop-blur-md"
            aria-live="polite"
          >
            <div className="flex flex-col items-center gap-4">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles className="h-8 w-8 text-primary" />
              </motion.div>
              <p className="text-base font-medium text-foreground/90">{t.rewriting}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
